import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2, Search } from '@/components/icons';
import { api } from '@/lib/api';
import { formatDateTime, timeAgo } from '@/lib/format';
import { Badge, Card, EmptyState, ErrorNotice, Input, PageHeader, PageLoader } from '@/components/ui';

/**
 * Every organization and the Project Admin who owns it, with its size. Admins are not part of any
 * organization, so this is an overview only - it never opens an organization's projects or reports.
 */
export function AdminOrganizationsPage() {
  const orgs = useQuery({ queryKey: ['admin-orgs'], queryFn: api.adminOrgs });
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (orgs.data ?? []).filter(
      (o) => !needle || o.name.toLowerCase().includes(needle) || o.owner.name.toLowerCase().includes(needle) || o.owner.email.toLowerCase().includes(needle),
    );
  }, [orgs.data, q]);

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Organizations"
        description="Each Project Admin owns one organization. Here you see who owns which, and how big it is - Admins don't take part in organizations."
      />
      {orgs.isPending ? (
        <PageLoader />
      ) : orgs.isError ? (
        <ErrorNotice error={orgs.error} onRetry={() => orgs.refetch()} />
      ) : orgs.data.length === 0 ? (
        <EmptyState
          icon={<Building2 className="size-6" />}
          title="No organizations yet"
          description="An organization appears here once a Project Admin creates one. Invite Project Admins from Administration > Invitations."
        />
      ) : (
        <Card>
          <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute top-1/2 left-4 size-3.5 -translate-y-1/2 text-fg-muted" />
              <Input aria-label="Search organizations" placeholder="Search organization or owner…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs tracking-wide text-slate-500 uppercase">
                  <th className="px-5 py-3 font-medium">Organization</th>
                  <th className="px-5 py-3 font-medium">Owner (Project Admin)</th>
                  <th className="px-5 py-3 text-right font-medium">Projects</th>
                  <th className="px-5 py-3 font-medium">People</th>
                  <th className="px-5 py-3 font-medium">Last activity</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/30">
                    <td className="px-5 py-3">
                      <div className="font-medium">{o.name}</div>
                      <div className="text-xs text-slate-500">Created {timeAgo(o.createdAt)}</div>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {o.owner.name}
                        {o.owner.status === 'DISABLED' && <Badge tone="warning">Disabled</Badge>}
                      </div>
                      <div className="text-xs text-slate-500">{o.owner.email}</div>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{o.projects}</td>
                    <td className="px-5 py-3">
                      <div className="tabular-nums">{o.members.total}</div>
                      <div className="text-xs text-slate-500">
                        {o.members.projectAdmins} Project Admin · {o.members.members} member{o.members.members === 1 ? '' : 's'} · {o.members.viewers} view only
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-500">
                      {o.lastActivityAt ? (
                        <Link
                          to="/admin/audit?tab=log"
                          className="underline-offset-2 hover:underline"
                          title={formatDateTime(o.lastActivityAt)}
                        >
                          {timeAgo(o.lastActivityAt)}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <p className="px-5 py-10 text-center text-sm text-slate-500">No organizations match your search.</p>}
          </div>
        </Card>
      )}
    </div>
  );
}
